import {
	type MemberRowTrim,
	planMemberRowTrim,
} from "../../domain/chimney/chimneyTrimmer";
import { roofFramePositions } from "../../domain/roof/roofFramePositions";
import {
	ENGINE_CC_M as CC,
	ENGINE_INTERIOR_BATTEN_CC_M,
	ENGINE_INTERIOR_BATTEN_W_M,
} from "../constants";
import { spreadEvenPositions } from "../geometry";
import type { FlueAxisM, SquareBaseBuildContext } from "./context";

/**
 * Name suffix of the far piece of a member cut around the flue. The near piece
 * keeps the member's own name; both are one board.
 */
export const TRIMMED_PIECE_SUFFIX = "-trimmed";

/**
 * The skorstensväxling's member layouts, without any Three.js: the roof and
 * ceiling builders draw from these and the BOM (`withChimneyTrimmer`) bills
 * from them, so the opening the model shows is the one the list buys.
 */

/**
 * The skorstensväxling in the roof joists: which joists a flue at `flue` cuts
 * and where the two headers go. Joists span X and are spread along Z, so the
 * plan's `along` is X and its `across` is Z. The end members (gable trusses,
 * rim joists) are never cut. One owner for the model and the BOM.
 */
export function planRoofJoistTrim(
	ctx: Pick<SquareBaseBuildContext, "L" | "fW">,
	flue: FlueAxisM | null | undefined,
): MemberRowTrim | null {
	if (!flue) return null;
	return planMemberRowTrim({
		positionsM: roofFramePositions(ctx.L, ctx.fW, CC),
		memberWidthM: ctx.fW,
		flueAlongM: flue.x,
		flueAcrossM: flue.z,
	});
}

/**
 * Plan extents of the structural ceiling — out to the inner faces of the
 * studs, i.e. the hammarband the takgles ends have to be nailed into. The
 * finished (panelled) ceiling is `iL + pT` smaller per side; layers that
 * are hidden behind the panel use these larger extents so nothing stops
 * short of its support.
 */
export function getCeilingStructuralExtents(ctx: SquareBaseBuildContext) {
	return {
		halfWidth: Math.max(0.12, ctx.W / 2 - ctx.sD),
		length: Math.max(0.2, ctx.L - 2 * ctx.sD),
	};
}

export interface CeilingBattenRow {
	x: number;
	name: string;
}

/**
 * Every takgles run, ascending in X. They run along Z — across the joists —
 * at a fixed x each, so a run is identified by its x alone.
 */
export function getCeilingBattenRows(
	ctx: SquareBaseBuildContext,
	namePrefix: string,
): CeilingBattenRow[] {
	const { halfWidth } = getCeilingStructuralExtents(ctx);
	const cc = ENGINE_INTERIOR_BATTEN_CC_M;
	const name = (label: string, index: number) =>
		`detail/interior-batten/${namePrefix}-ceiling-${label}-${index + 1}`;
	if (ctx.roofConfig.kind === "gable") {
		const half = spreadEvenPositions(0, halfWidth, cc);
		return [
			...half
				.map((x) => -x)
				.reverse()
				.map((x, index) => ({ x, name: name("left", index) })),
			// Drop the ridge batten from the right half; the left half already
			// placed one at x = 0 and two coincident boxes would z-fight.
			...half.slice(1).map((x, index) => ({ x, name: name("right", index) })),
		];
	}
	return spreadEvenPositions(-halfWidth, halfWidth, cc).map((x, index) => ({
		x,
		name: name("top", index),
	}));
}

/**
 * The skorstensväxling in the takgles: runs a flue at `flue` would pass within
 * 100 mm of are cut, and two noggins (kortlingar) across X carry the panel
 * ends between the nearest uncut runs — or out to the hammarband when the cut
 * run is the outermost one. One owner for the model and the BOM.
 */
export function planCeilingBattenTrim(
	ctx: SquareBaseBuildContext,
	flue: FlueAxisM | null | undefined,
): MemberRowTrim | null {
	if (!flue) return null;
	const { halfWidth } = getCeilingStructuralExtents(ctx);
	return planMemberRowTrim({
		positionsM: getCeilingBattenRows(ctx, "").map((row) => row.x),
		memberWidthM: ENGINE_INTERIOR_BATTEN_W_M,
		flueAlongM: flue.z,
		flueAcrossM: flue.x,
		acrossLimitsM: [-halfWidth, halfWidth],
	});
}
